import { supabase } from "./supabase";

const API_BASE = process.env.REACT_APP_API_BASE || "";

export const PRODUCT_EVENTS = Object.freeze({
  app_opened: "app_opened",
  chat_message_sent: "chat_message_sent",
  onboarding_started: "onboarding_started",
  mail_setup_offered: "mail_setup_offered",
  extracted_event_corrected: "extracted_event_corrected",
  extracted_event_deleted: "extracted_event_deleted",
  school_link_opened: "school_link_opened",
  school_name_submitted: "school_name_submitted",
  school_match_confirmed: "school_match_confirmed",
  school_name_unmatched: "school_name_unmatched",
});

export const HANDLED_BY = Object.freeze({
  scripted_route: "scripted_route",
  facts_pack: "facts_pack",
  llm_fallback: "llm_fallback",
  miss: "miss",
});

/** Mail/photo extraction origins on saved_schedule_items (vs manual Merken). */
export function isExtractionOrigin(originOrItem) {
  const origin =
    typeof originOrItem === "string"
      ? originOrItem
      : originOrItem?.origin || originOrItem?.sourceKind || null;
  return origin === "mail" || origin === "capture";
}

/**
 * @param {string|null|undefined} createdAt
 * @param {number} [nowMs]
 * @returns {number|null}
 */
export function minutesSinceCreation(createdAt, nowMs = Date.now()) {
  const t = createdAt ? new Date(createdAt).getTime() : NaN;
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((nowMs - t) / 60000));
}

/**
 * Accuracy-relevant fields from a patch/override body (excludes remind/visibility/done).
 * @param {Record<string, unknown>|null|undefined} body
 * @returns {Array<'date'|'time'|'title'|'child'>}
 */
export function extractionCorrectionFields(body) {
  if (!body || typeof body !== "object") return [];
  /** @type {Array<'date'|'time'|'title'|'child'>} */
  const fields = [];
  if (body.title !== undefined) fields.push("title");
  if (body.date !== undefined) fields.push("date");
  if (body.time !== undefined || body.all_day !== undefined) fields.push("time");
  if (body.child !== undefined) fields.push("child");
  return fields;
}

/**
 * Fire-and-forget accuracy events for an extraction-sourced schedule item patch.
 * @param {{
 *   item?: { id?: string, createdAt?: string, origin?: string, sourceKind?: string } | null,
 *   body?: Record<string, unknown> | null,
 * }} args
 */
export function logExtractedEventMutation({ item, body } = {}) {
  if (!item?.id || !isExtractionOrigin(item)) return;
  const minutes = minutesSinceCreation(item.createdAt);
  const base = {
    event_id: item.id,
    ...(minutes !== null ? { minutes_since_creation: minutes } : {}),
  };

  if (body?.dismiss === true) {
    logProductEvent(PRODUCT_EVENTS.extracted_event_deleted, base);
    return;
  }

  for (const field of extractionCorrectionFields(body)) {
    logProductEvent(PRODUCT_EVENTS.extracted_event_corrected, {
      ...base,
      field_changed: field,
    });
  }
}

/**
 * Map resolveReply result → handled_by for chat_message_sent.
 * Companion sources: local_facts → facts_pack, llm → llm_fallback, else miss.
 * Client router matches (no companionSource) → scripted_route; unmatched stub → miss.
 *
 * @param {{ companionSource?: string, unmatched?: boolean } | null | undefined} reply
 * @returns {typeof HANDLED_BY[keyof typeof HANDLED_BY]}
 */
export function mapChatHandledBy(reply) {
  const source = reply?.companionSource;
  if (source) {
    if (source === "local_facts") return HANDLED_BY.facts_pack;
    if (source === "llm") return HANDLED_BY.llm_fallback;
    return HANDLED_BY.miss;
  }
  if (reply?.unmatched) return HANDLED_BY.miss;
  if (reply) return HANDLED_BY.scripted_route;
  return HANDLED_BY.miss;
}

/**
 * Fire-and-forget product event. Never throws; does not block the caller.
 * No-ops when signed out. Swallows errors with console.warn.
 *
 * @param {string} eventName
 * @param {Record<string, unknown>} [props]
 */
export function logProductEvent(eventName, props = {}) {
  void (async () => {
    try {
      const name = String(eventName || "").trim();
      if (!name) return;
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/product-events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          event_name: name,
          props:
            props && typeof props === "object" && !Array.isArray(props) ? props : {},
        }),
      });
      if (!res.ok) {
        console.warn("logProductEvent:", name, res.status);
      }
    } catch (err) {
      console.warn("logProductEvent:", err?.message || err);
    }
  })();
}
